/**
 * Food search hooks for platform UIs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { queries } from "@corpuscomplete/core/zero";
import { useQuery as useZeroQuery } from "@corpuscomplete/zero-data-transformer/react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { Food } from "@corpuscomplete/core/nutrition/food";
import type { FoodCategory } from "@corpuscomplete/core/nutrition/food-category";
import { FoodSearch } from "@corpuscomplete/core/nutrition/food-search";
import { FoodLog } from "@corpuscomplete/core/nutrition/food-log";
import { Rpc } from "../../../rpc";

export namespace FoodSearchUI {
  export const limit = 20;

  const unexpandedLimit = 5;
  const DEBOUNCE_MS = 200;

  export type Row = {
    key: string;
    item: Food.Item;
    preparations: Array<{ key: string; item: Food.Item }>;
    preparationCountLabel: string | undefined;
  };

  function foodItemKey(item: Food.Item) {
    return `${item.food.id}:${item.food.version}`;
  }

  function groupedItemToRow(item: FoodSearch.SearchGroupedItem): Row {
    const preparations = item.preparations.map((preparation) => ({
      key: foodItemKey(preparation),
      item: preparation,
    }));
    return {
      key: foodItemKey(item),
      item,
      preparations,
      preparationCountLabel:
        preparations.length === 1
          ? "+1 Preparation"
          : preparations.length > 1
            ? `+${preparations.length} Preparations`
            : undefined,
    };
  }

  export type Section = {
    title: string;
    data: Row[];
    expandButton?: {
      label: string;
      onToggle: () => void;
    };
  };

  type CategoryFilterMode = "include" | "exclude";
  type CategoryFilterArgs = {
    categoryId: string;
    association: FoodCategory.AssociationType;
    mode: CategoryFilterMode;
  };

  export type SearchState = {
    text: string;
    onChangeText: (text: string) => void;
    categories: FoodSearch.SearchParams["categories"];
    setCategories: (categories: FoodSearch.SearchParams["categories"]) => void;
    categoryIds: string[];
    addCategoryFilter: (args: CategoryFilterArgs) => void;
    removeCategoryFilter: (args: CategoryFilterArgs) => void;
    toggleCategoryFilter: (args: CategoryFilterArgs) => void;
    clearCategoryFilters: () => void;
    reset: () => void;
  };

  function filterKeyForMode(mode: CategoryFilterMode) {
    return mode === "include" ? "includeIds" : "excludeIds";
  }

  function oppositeFilterKeyForMode(mode: CategoryFilterMode) {
    return mode === "include" ? "excludeIds" : "includeIds";
  }

  function addCategoryToFilter(
    categories: FoodSearch.SearchParams["categories"],
    args: CategoryFilterArgs,
  ): FoodSearch.SearchParams["categories"] {
    const key = filterKeyForMode(args.mode);
    const oppositeKey = oppositeFilterKeyForMode(args.mode);
    const association = categories?.[args.association] ?? {};
    const ids = [...(association[key] ?? []), args.categoryId];
    const oppositeIds = (association[oppositeKey] ?? []).filter((id) => id !== args.categoryId);
    return {
      ...categories,
      [args.association]: {
        ...association,
        [key]: ids,
        [oppositeKey]: oppositeIds,
      },
    };
  }

  function removeCategoryFromFilter(
    categories: FoodSearch.SearchParams["categories"],
    args: CategoryFilterArgs,
  ): FoodSearch.SearchParams["categories"] {
    const key = filterKeyForMode(args.mode);
    const association = categories?.[args.association] ?? {};
    return {
      ...categories,
      [args.association]: {
        ...association,
        [key]: (association[key] ?? []).filter((id) => id !== args.categoryId),
      },
    };
  }

  function categoryIds(categories: FoodSearch.SearchParams["categories"]): string[] {
    return [
      ...(categories?.isA?.includeIds ?? []),
      ...(categories?.isA?.excludeIds ?? []),
      ...(categories?.contains?.includeIds ?? []),
      ...(categories?.contains?.excludeIds ?? []),
    ];
  }

  export function useSearch(args: {
    text: string;
    categories?: FoodSearch.SearchParams["categories"];
    limit?: number;
    showAllResults?: boolean;
  }) {
    const rpc = Rpc.useRpc();
    const [debouncedText] = useDebouncedValue(args.text, { wait: DEBOUNCE_MS });
    const [recipesExpanded, setRecipesExpanded] = useState(false);
    const [genericsExpanded, setGenericsExpanded] = useState(false);
    const [brandedExpanded, setBrandedExpanded] = useState(false);

    useEffect(() => {
      setRecipesExpanded(false);
      setGenericsExpanded(false);
      setBrandedExpanded(false);
    }, [args.text]);

    const { data, isLoading } = useQuery({
      ...rpc.foodSearch.search.queryOptions({
        input: {
          text: debouncedText,
          limit: args.limit ?? limit,
          categories: args.categories,
        },
      }),
      enabled: debouncedText.length >= 3,
    });

    const sections: Section[] = [];
    if (!isLoading && data) {
      if (data.recipes.length > 0) {
        const recipeRows = data.recipes.map(groupedItemToRow);
        sections.push({
          title: "Recipes",
          data:
            args.showAllResults || recipesExpanded
              ? recipeRows
              : recipeRows.slice(0, unexpandedLimit),
          expandButton:
            args.showAllResults || recipeRows.length <= unexpandedLimit
              ? undefined
              : {
                  label: recipesExpanded
                    ? "Show Less"
                    : `Show ${recipeRows.length - unexpandedLimit} more`,
                  onToggle: () => setRecipesExpanded(!recipesExpanded),
                },
        });
      }
      if (data.generic.length > 0) {
        const genericRows = data.generic.map(groupedItemToRow);
        sections.push({
          title: "Generic",
          data:
            args.showAllResults || genericsExpanded
              ? genericRows
              : genericRows.slice(0, unexpandedLimit),
          expandButton:
            args.showAllResults || genericRows.length <= unexpandedLimit
              ? undefined
              : {
                  label: genericsExpanded
                    ? "Show Less"
                    : `Show ${genericRows.length - unexpandedLimit} more`,
                  onToggle: () => setGenericsExpanded(!genericsExpanded),
                },
        });
      }
      if (data.branded.length > 0) {
        const brandedRows = data.branded.map(groupedItemToRow);
        sections.push({
          title: "Branded",
          data:
            args.showAllResults || brandedExpanded
              ? brandedRows
              : brandedRows.slice(0, unexpandedLimit),
          expandButton:
            args.showAllResults || brandedRows.length <= unexpandedLimit
              ? undefined
              : {
                  label: brandedExpanded
                    ? "Show Less"
                    : `Show ${brandedRows.length - unexpandedLimit} more`,
                  onToggle: () => setBrandedExpanded(!brandedExpanded),
                },
        });
      }
    }

    return {
      sections,
      isLoading,
    };
  }

  export function useSearchState(args?: {
    initialText?: string;
    initialCategories?: FoodSearch.SearchParams["categories"];
  }): SearchState {
    const [text, setText] = useState(args?.initialText ?? "");
    const [categories, setCategories] = useState<FoodSearch.SearchParams["categories"]>(
      args?.initialCategories ?? {},
    );

    const addCategoryFilter = useCallback((filterArgs: CategoryFilterArgs) => {
      setCategories((current) => addCategoryToFilter(current, filterArgs));
    }, []);

    const removeCategoryFilter = useCallback((filterArgs: CategoryFilterArgs) => {
      setCategories((current) => removeCategoryFromFilter(current, filterArgs));
    }, []);

    const toggleCategoryFilter = useCallback((filterArgs: CategoryFilterArgs) => {
      setCategories((current) => {
        const key = filterKeyForMode(filterArgs.mode);
        const selected = current?.[filterArgs.association]?.[key]?.includes(filterArgs.categoryId);
        return selected
          ? removeCategoryFromFilter(current, filterArgs)
          : addCategoryToFilter(current, filterArgs);
      });
    }, []);

    const clearCategoryFilters = useCallback(() => {
      setCategories({});
    }, []);

    const reset = useCallback(() => {
      setText("");
      setCategories({});
    }, []);

    return {
      text,
      onChangeText: setText,
      categories,
      setCategories,
      categoryIds: categoryIds(categories),
      addCategoryFilter,
      removeCategoryFilter,
      toggleCategoryFilter,
      clearCategoryFilters,
      reset,
    };
  }

  export function useSuggestions({ userId, time }: { userId: string; time: Temporal.PlainTime }) {
    const [entries, details] = useZeroQuery(queries.foodLog.forSuggestions({ userId }), {
      ttl: "1m",
    });

    const suggestions = useMemo(() => {
      return FoodLog.suggestions(Food.asItems(entries), time);
    }, [entries, time]);

    return {
      suggestions,
      isLoading: details.type === "unknown",
    };
  }
}
